import {
  Button,
  Card,
  CustomImage,
  DatePicker,
  IconDocument,
  OptionInterface,
  Select,
  SelectOption,
  Table,
  TableColumns,
  TableData,
  TablePagination,
} from "@veneer/core";
import "../../assets/css/views/metrics/metricsRealTime.css";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { useTranslation } from "react-i18next";
import moment from "moment";
import dataListService from "../../services/generalApi/dataList.service";
import metricSearchSelect from "./MetricsComponent/metricSearchSelect";
import metricsRealTimeService from "../../services/surveyApi/metricsRealTime.service";
import IconUserNotFound from "../../assets/img/attendance/VectoruserNotFound.png";
import {IOptionsMetricsRealTime} from "../../models/input/surveyApi/IOptionsMetrics";
import { IValuesMetricsRealTime } from "../../models/input/surveyApi/IValuesMetrics";

export default function MetricsRealTime() {
  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const { handleSearch } = metricSearchSelect<IOptionsMetricsRealTime>();
  const [isButtonValid, setIsButtonValid] = useState<boolean>(true);

  const initialOptions: IOptionsMetricsRealTime = {
    Coordinator: [],
    Supervisor: [],
    Metrics: [],
    FilteredCoordinator: [],
    FilteredSupervisor: [],
    FilteredMetrics: [],
  };

  const initialValidation = {
    Coordinator: true,
    Supervisor: true,
    Metrics: true,
  };

  const initialForm = {
    SupervisorID: 0,
    Metrics: "",
  };

  const initialValues: IValuesMetricsRealTime = {
    Supervisor: [],
    Coordinator: [],
    Metrics: [],
    StartDate: null,
    EndDate: null,
    Columns: [],
  };

  const [optionSelect, setOptionSelect] = useState<IOptionsMetricsRealTime>({
    ...initialOptions,
  });
  const [handleValidate, setHandleValidate] = useState({
    ...initialValidation,
  });
  const [form, setForm] = useState({ ...initialForm });
  const [values, setValues] = useState({ ...initialValues });

  const updateOptions = (key: keyof IOptionsMetricsRealTime, value: SelectOption[]) => {
    setOptionSelect((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const memoizedHandleValidate = useMemo(
    () => handleValidate,
    [handleValidate]
  );

  useEffect(() => {
    const hasAtLeastOneFalse = Object.values(memoizedHandleValidate).some(
      (value) => value === true
    );

    if (hasAtLeastOneFalse) {
      return setIsButtonValid(true);
    } else {
      return setIsButtonValid(false);
    }
  }, [memoizedHandleValidate]);

  useEffect(() => {
    getCoordinatorBySubRegion();
    getMetricsList();
    generateDate();
  }, []);

  const columnConfigMap: Record<string, TableColumns[]> = {
    GetMetricRealTimeAbsence: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "promotor", label: t("header.promoter") },
      { id: "dias_De_CheckIn", label: t("header.CheckInDays") },
      { id: "dias_Laborales", label: t("header.WorkingDays") },
      { id: "dias_De_Ausencias", label: t("header.AbsenceDays") },
      { id: "status", label: t("header.status") },
    ],
    GetMetricRealTimeAbsenceScheduling: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "promotor", label: t("header.promoter") },
      { id: "ausencias", label: t("header.absences") },
      { id: "dias_Laborales", label: t("header.workedDays") },
      { id: "status", label: t("header.status") },
      { id: "descripcion", label: t("header.description") },
    ],
    GetMetricRealTimeCheckIn: [
      { id: "date", label: t("header.date") },
      { id: "photo", label: t("header.photo") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "promotor", label: t("header.promoter") },
      { id: "posCode", label: t("header.posOfCode") },
      { id: "punto_De_Venta", label: t("header.pointOfSale") },
      { id: "checkIn_Programado", label: t("header.scheduledCheckIn") },
      { id: "fecha_De_Registro", label: t("header.registrationDate") },
      { id: "diferencia_En_Minutos", label: t("header.differenceInMinutes") },
      { id: "status", label: t("header.status") },
    ],
    GetMetricRealTimeEsfCoverage: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "posCode", label: t("header.posOfCode") },
      { id: "pos", label: t("header.pos") },
      { id: "subtotal", label: "Sub-Total" },
      { id: "total", label: t("header.total") },
      { id: "metrica", label: t("header.metrics") },
    ],
    GetMetricRealTimeSosPc: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "posCode", label: t("header.posOfCode") },
      { id: "pos", label: t("header.pos") },
      { id: "subtotal", label: "Sub-Total" },
      { id: "total", label: t("header.total") },
      { id: "metrica", label: t("header.metrics") },
    ],
    GetMetricRealTimeSosPrint: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "posCode", label: t("header.posOfCode") },
      { id: "pos", label: t("header.pos") },
      { id: "subtotal", label: "Sub-Total" },
      { id: "total", label: t("header.total") },
      { id: "metrica", label: t("header.metrics") },
    ],
    GetMetricRealTimeSync: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "promotor", label: t("header.promoter") },
      { id: "dias_Sincronizados", label: t("header.synchronizedDays") },
      { id: "dias_Laborales", label: t("header.workedDays") },
      { id: "porcentaje", label: t("header.percentage") },
      { id: "status", label: t("header.status") },
    ],
    GetMetricRealTimeTrainigMaterial: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "promotor", label: t("header.promoter") },
      {
        id: "cantidad_De_Material_De_Entrenamiento",
        label: t("header.quantityTraining"),
      },
      { id: "dias_Laborales", label: t("header.workedDays") },
      { id: "ausencias", label: t("header.absences") },
      { id: "porcentaje", label: t("header.percentage") },
      { id: "status", label: t("header.status") },
    ],
    GetMetricRealTimeVisualPcStatus: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "posCode", label: t("header.posOfCode") },
      { id: "pos", label: t("header.pos") },
      { id: "cantidad_de_HP", label: t("header.hpQuantity") },
      {
        id: "cantidad_de_Competencia",
        label: t("header.quantityOfCompetition"),
      },
      { id: "status", label: t("header.status") },
    ],
    GetMetricRealTimeVisualPrintStatus: [
      { id: "date", label: t("header.date") },
      { id: "supervisor", label: t("header.supervisor") },
      { id: "posCode", label: t("header.posOfCode") },
      { id: "pos", label: t("header.pos") },
      { id: "cantidad_de_HP", label: t("header.hpQuantity") },
      {
        id: "cantidad_de_Competencia",
        label: t("header.quantityOfCompetition"),
      },
      { id: "status", label: t("header.status") },
    ],
  };

  const getTableColumns = (name: string): TableColumns[] => {
    return columnConfigMap[name];
  };

  const pagedData = tableData.slice(
    (currentPage - 1) * pageSize,
    (currentPage - 1) * pageSize + pageSize
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    option: OptionInterface<number>
  ) => {
    setPageSize(option.value);
  };

  const paginationProps: TablePagination = {
    currentPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSize,
    totalItems: tableData.length,
  };

  const generateDate = () => {
    const today = moment();
    const monday = today.clone().startOf("isoWeek");
    setValues((prevState: any) => ({
      ...prevState,
      StartDate: monday,
      EndDate: today,
    }));
  };

  const getCoordinatorBySubRegion = () => {
    dataListService.getCoordinatorBySubRegion().then((result: any[]) => {
      const updatedOptions = result.map((item) => ({
        value: item.coordinatorId,
        label: item.fullName,
      }));
      updateOptions("Coordinator", updatedOptions);
      updateOptions("FilteredCoordinator", updatedOptions);
    });
  };

  const getSupervisorByCoordinator = (CoordinatorId: number) => {
    dataListService
      .getSupervisorByCoordinator(CoordinatorId)
      .then((result: any[]) => {
        const updatedOptions = result.map((item) => ({
          value: item.supervisorID,
          label: item.fullName,
        }));
        if (updatedOptions[0].value === 0) {
          updatedOptions[0].label = t("header.noSupervisorsAssigned");
        }
        updateOptions("Supervisor", updatedOptions);
        updateOptions("FilteredSupervisor", updatedOptions);
      });
  };

  const getMetricsList = () => {
    metricsRealTimeService
      .GetMetricsList(languageID.value)
      .then((result: any[]) => {
        const updatedOptions = result.map((item) => ({
          value: item.controllers,
          label: item.metrics,
        }));
        updateOptions("Metrics", updatedOptions);
        updateOptions("FilteredMetrics", updatedOptions);
      });
  };

  const onChangeCoordinator = (
    selectedOption: SelectOption,
    setFormValues: React.Dispatch<React.SetStateAction<any>>,
    setHandleValidate: React.Dispatch<React.SetStateAction<any>>,
    setForm: React.Dispatch<React.SetStateAction<any>>
  ) => {
    setFormValues((prevState: any) => ({
      ...prevState,
      Coordinator: [selectedOption.value],
      Supervisor: [],
    }));
    setForm((prevState: any) => ({
      ...prevState,
      SupervisorID: 0,
      CoordinatorId: selectedOption.value,
    }));
    setHandleValidate((prevState: any) => ({
      ...prevState,
      Coordinator: false,
    }));
    getSupervisorByCoordinator(Number(selectedOption.value));
  };

  const onChangeSupervisor = (
    selectedOption: SelectOption,
    setFormValues: React.Dispatch<React.SetStateAction<any>>,
    setHandleValidate: React.Dispatch<React.SetStateAction<any>>,
    setForm: React.Dispatch<React.SetStateAction<any>>
  ) => {
    setFormValues((prevState: any) => ({
      ...prevState,
      Supervisor: [selectedOption.value],
    }));
    setForm((prevState: any) => ({
      ...prevState,
      SupervisorID: selectedOption.value,
    }));
    setHandleValidate((prevState: any) => ({
      ...prevState,
      Supervisor: false,
    }));

    if (selectedOption.value === 0) {
      setHandleValidate((prevState: any) => ({
        ...prevState,
        Supervisor: true,
      }));
    }
  };

  const onChangeMetrics = (
    selectedOption: SelectOption,
    setFormValues: React.Dispatch<React.SetStateAction<any>>,
    setHandleValidate: React.Dispatch<React.SetStateAction<any>>,
    setForm: React.Dispatch<React.SetStateAction<any>>
  ) => {
    setFormValues((prevState: any) => ({
      ...prevState,
      Metrics: [selectedOption.value],
    }));
    setForm((prevState: any) => ({
      ...prevState,
      Metrics: selectedOption.value,
    }));
    setHandleValidate((prevState: any) => ({
      ...prevState,
      Metrics: false,
    }));
    setValues((prevState: any) => ({
      ...prevState,
      Columns: [],
    }));
  };

  const disableSupervisor = (values: any) => {
    if (values.Coordinator[0] !== undefined) {
      return false;
    } else {
      return true;
    }
  };

  function onClickObtainMetrics(downloadMetric: boolean): void {
    setCurrentPage(1);
    
    if (downloadMetric) {
      const nameExcel = form.Metrics.replace("Get", "") + ".xlsx";

      metricsRealTimeService
        .downloadMetric(
          languageID.value,
          form.SupervisorID,
          form.Metrics.replace("Get", "Download")
        )
        .then((result: any) => {
          const url = window.URL.createObjectURL(new Blob([result]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", nameExcel);
          document.body.appendChild(link);
          link.click();
        });
      return;
    }

    setTableData([]);
    const column = getTableColumns(form.Metrics);
    setValues((prevState: any) => ({
      ...prevState,
      Columns: column,
    }));
    setLoading(true);

    metricsRealTimeService
      .GetMetricsRealTime(form.SupervisorID, form.Metrics)
      .then((result: any[]) => {
        const updatedTableData = result.map((item) => {
          item.dias_De_Ausencias = String(item.dias_De_Ausencias);
          item.dias_De_CheckIn = String(item.dias_De_CheckIn);
          item.dias_Laborales = String(item.dias_Laborales);
          item.ausencias = String(item.ausencias);
          item.subtotal = String(item.subtotal);
          item.total = String(item.total);
          item.metrica = String(item.metrica) + "%";
          item.porcentaje = String(item.porcentaje) + "%";
          item.cantidad_de_HP = String(item.cantidad_de_HP);
          item.cantidad_de_Competencia = String(item.cantidad_de_Competencia);
          item.dias_Sincronizados = String(item.dias_Sincronizados);
          item.cantidad_De_Material_De_Entrenamiento = String(
            item.cantidad_De_Material_De_Entrenamiento
          );

          const row: Record<string, any> = {};
          column.forEach((col) => {
            if (col.id === "date") {
              row[col.id] = item[col.id].split("T")[0];
            } else if (col.id === "photo") {
              if (item[col.id] === "") {
                row[col.id] = (
                  <div className="DivIconCustomNoItemsRealTime">
                    <CustomImage
                      size={55}
                      quietArea={10}
                      src={IconUserNotFound}
                    />
                  </div>
                );
              } else {
                row[col.id] = (
                  <CustomImage size={100} quietArea={0} src={item[col.id]} />
                );
              }
            } else {
              row[col.id] = item[col.id] || "";
            }
          });
          return {
            ...row,
            rowConfig: {
              disabled: false,
              noWrap: false,
              selected: false,
            },
          };
        });

        setTableData(updatedTableData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  return (
    <>
      <div style={{ padding: "1%" }}>
        <h4>{t("header.realTimeMetrics")}</h4>
      </div>

      <div className="realTime-container">
        <Select
          className="realTime-select"
          clearIcon={false}
          options={optionSelect.Coordinator}
          id="selectCoordinator"
          label={t("header.coordinator")}
          placement="bottom"
          helperTextVisibility="auto"
          onChange={(selectedOption: SelectOption) =>
            onChangeCoordinator(
              selectedOption,
              setValues,
              setHandleValidate,
              setForm
            )
          }
          value={values.Coordinator}
          required={handleValidate.Coordinator}
          onSearch={(query: string) =>
            handleSearch(
              query,
              "Coordinator",
              optionSelect.FilteredCoordinator,
              setOptionSelect
            )
          }
          searchDelay={500}
          showSearch={true}
        />
        <Select
          className="realTime-select"
          clearIcon={false}
          options={optionSelect.Supervisor}
          id="selectSupervisor"
          label={t("header.supervisor")}
          placement="bottom"
          helperTextVisibility="auto"
          onChange={(selectedOption: SelectOption) =>
            onChangeSupervisor(
              selectedOption,
              setValues,
              setHandleValidate,
              setForm
            )
          }
          value={values.Supervisor}
          disabled={disableSupervisor(values)}
          required={handleValidate.Supervisor}
          onSearch={(query: string) =>
            handleSearch(
              query,
              "Supervisor",
              optionSelect.FilteredSupervisor,
              setOptionSelect
            )
          }
          searchDelay={500}
          showSearch={true}
        />
        <Select
          className="realTime-select"
          clearIcon={false}
          options={optionSelect.Metrics}
          id="selectMetrics"
          label={t("header.metrics")}
          placement="bottom"
          helperTextVisibility="auto"
          onChange={(selectedOption: SelectOption) =>
            onChangeMetrics(
              selectedOption,
              setValues,
              setHandleValidate,
              setForm
            )
          }
          value={values.Metrics}
          required={handleValidate.Metrics}
          onSearch={(query: string) =>
            handleSearch(
              query,
              "Metrics",
              optionSelect.FilteredMetrics,
              setOptionSelect
            )
          }
          searchDelay={500}
          showSearch={true}
        />
        <DatePicker
          className="realTime-datePicker"
          endDate={values.EndDate ? values.EndDate.toISOString() : undefined}
          format="ll"
          clearIcon={false}
          range
          startDate={
            values.StartDate ? values.StartDate.toISOString() : undefined
          }
          startDateLabel="Start Date"
          endDateLabel="End Date"
          disabled={true}
        />

        <Button
          className="realTime-button"
          disabled={isButtonValid}
          onClick={() => onClickObtainMetrics(false)}
        >
          {t("header.obtainMetrics")}
        </Button>

        <div className="tooltipRealTime">
          <span className="tooltiptextRealTime">
            {t("header.downloadMetrics")}
          </span>
          <Button
            onClick={() => onClickObtainMetrics(true)}
            style={{ margin: "2px" }}
            appearance="secondary"
            leadingIcon={<IconDocument />}
            disabled={isButtonValid}
          ></Button>
        </div>
      </div>

      {values.Columns.length > 0 && (
        <div style={{ padding: "20px" }}>
          <Card
            appearance="outlined"
            className="divTableSellOut"
            content={
              <Table
                className="tableSellOut"
                behavior={{
                  type: "singlePage",
                }}
                columnResize
                loading={loading}
                columns={values.Columns}
                data={pagedData}
                loadingDataLength={5}
                pagination={paginationProps}
              />
            }
          />
        </div>
      )}
    </>
  );
}
